import React, { type ReactNode } from 'react';

import LinkifyIt from 'linkify-it';
import tlds from 'tlds';

import type { FCC } from '../../types/common';

import styles from './linkify.module.css';

const linkify = new LinkifyIt({});
const mention = /@.+?\[(email:|user_id:|group_id:)([^\]]+)\]/g;

// Set top-level domains list and disable parsing 'ftp:', '//', 'mailto:' schemas.
linkify.tlds(tlds).add('ftp:', null).add('//', null).add('mailto:', null);

const findPrefixIndex = (mentionItem: string) => {
  const userIdIndex = mentionItem.indexOf('[user_id');
  if (userIdIndex > 0) {
    return userIdIndex;
  }

  const emailIndex = mentionItem.indexOf('[email');
  if (emailIndex > 0) {
    return emailIndex;
  }

  const groupIdIndex = mentionItem.indexOf('[group_id');
  if (groupIdIndex > 0) {
    return groupIdIndex;
  }

  return -1;
};
const parseString = (
  string: string,
  forcedLabel?: string,
): string | string[] => {
  if (string === '') {
    return string;
  }

  const matches = linkify.match(string) ?? [];
  const matchItems = [...string.matchAll(mention), ...matches].sort((a, b) => {
    const indexA = a?.index;
    const indexB = b?.index;
    if (!indexA || !indexB) {
      return 0;
    }
    if (indexA < indexB) {
      return -1;
    }
    if (indexA > indexB) {
      return 1;
    }

    return 0;
  });
  if (matchItems.length === 0) {
    return string;
  }

  const elements = [];
  let lastIndex = 0;

  if (matchItems.length > 0) {
    matchItems.forEach((match, index) => {
      const mentionItem = '0' in match ? match[0] : null;
      const mentionIndex = match && Number(match.index);
      // Push preceding text if there is any
      if (match.index && match.index > lastIndex) {
        elements.push(string.substring(lastIndex, match.index));
      }

      if ('url' in match) {
        elements.push(
          <a
            target="_blank"
            rel="nofollow noopener noreferrer"
            href={match?.url}
            key={index}
          >
            {forcedLabel || match?.text}
          </a>,
        );
      } else if (mentionItem) {
        const matchEnd = findPrefixIndex(mentionItem);

        const display = mentionItem.slice(
          mentionItem.indexOf('@') + 1,
          matchEnd > 0 ? matchEnd : undefined,
        );

        elements.push(
          <span className={styles.mention_item} key={index}>
            {display}
          </span>,
        );
      }
      const lastItemIndex =
        'lastIndex' in match
          ? match.lastIndex
          : mentionIndex + (mentionItem ? mentionItem.length : 0);

      lastIndex = lastItemIndex;
    });
  }

  if (string.length > lastIndex) {
    elements.push(string.substring(lastIndex));
  }

  return elements.length === 1 ? elements[0] : elements;
};

const parse = (children: ReactNode, key = 0, label?: string): ReactNode => {
  if (typeof children === 'string') {
    return parseString(children, label);
  }

  if (
    React.isValidElement<{ children?: ReactNode }>(children) &&
    children.type !== 'a' &&
    children.type !== 'button'
  ) {
    return React.cloneElement(
      children,
      { key },
      parse(children.props.children, key, label),
    );
  }
  if (Array.isArray(children)) {
    return children.map((child, i) => parse(child, i, label));
  }

  return children;
};

const Linkify: FCC<{ forcedLabel?: string }> = (props) => (
  <>{parse(props.children, 0, props.forcedLabel)}</>
);

export default Linkify;
